/* ==========================================================================
   VastraGharr Luxe 3.0 - global behaviours
   window.VG namespace: storage, toast, announcer, clipboard, wishlist store +
   drawer, <vg-slider>, bottom nav (cart drawer / search), WhatsApp float,
   reveal on scroll ([data-vg-reveal]), image skeleton + fade (.vg-img),
   cross-document view transitions (card photo -> product photo), back to top,
   shared button states (VG.buttonState).
   Vanilla ES2019, no dependencies. Uses Dawn globals when present
   (subscribe / PUB_SUB_EVENTS, trapFocus / removeTrapFocus).
   ========================================================================== */
(function () {
  'use strict';

  var VG = (window.VG = window.VG || {});
  if (VG.themeLoaded) return;
  VG.themeLoaded = true;
  VG.version = '3.0.0';

  var config = VG.config || {};
  var strings = config.strings || {};
  var designMode = !!(window.Shopify && window.Shopify.designMode);
  var docEl = document.documentElement;
  docEl.classList.add('js-vg');
  var shopRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  if (shopRoot.charAt(shopRoot.length - 1) !== '/') shopRoot += '/';

  VG.routes = {
    root: shopRoot,
    cart: config.cartUrl || shopRoot + 'cart',
    search: config.searchUrl || shopRoot + 'search',
    allProducts: config.allProductsUrl || shopRoot + 'collections/all',
  };

  var ICONS = {
    check: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="m5 12.5 4.5 4.5L19 7.5"/></svg>',
    heart: '<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M12 20.25s-7.75-4.6-7.75-10.4A4.35 4.35 0 0 1 8.6 5.5c1.42 0 2.68.69 3.4 1.8a4.03 4.03 0 0 1 3.4-1.8 4.35 4.35 0 0 1 4.35 4.35c0 5.8-7.75 10.4-7.75 10.4Z"/></svg>',
    heartOutline: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M12 20.25s-7.75-4.6-7.75-10.4A4.35 4.35 0 0 1 8.6 5.5c1.42 0 2.68.69 3.4 1.8a4.03 4.03 0 0 1 3.4-1.8 4.35 4.35 0 0 1 4.35 4.35c0 5.8-7.75 10.4-7.75 10.4Z"/></svg>',
    info: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="8.25"/><path d="M12 11v5M12 8h.01"/></svg>',
    close: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="m6 6 12 12M18 6 6 18"/></svg>',
  };
  VG.icons = ICONS;

  /* ---------- Small utilities ---------- */
  /* Shop wording from the theme editor: JSON in [data-vg-strings] attributes printed by Liquid
     (snippets vg-wishlist-drawer and vg-bottom-nav, from Theme settings > VastraGharr features).
     It wins over VG.config.strings; a missing or empty value keeps the built-in English. */
  var shopStrings = null;
  function readShopStrings() {
    if (shopStrings) return shopStrings;
    var found = {};
    toArray(document.querySelectorAll('[data-vg-strings]')).forEach(function (host) {
      var data = null;
      try {
        data = JSON.parse(host.getAttribute('data-vg-strings') || '{}');
      } catch (e) {
        data = null; /* a malformed attribute is ignored: the defaults still apply */
      }
      if (!data || typeof data !== 'object') return;
      Object.keys(data).forEach(function (key) {
        if (typeof data[key] === 'string' && data[key].trim()) found[key] = data[key];
      });
    });
    if (document.readyState !== 'loading') shopStrings = found;
    return found;
  }

  function t(key, fallback) {
    return readShopStrings()[key] || strings[key] || fallback;
  }

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  function closestEl(target, selector) {
    if (!target || typeof target.closest !== 'function') return null;
    return target.closest(selector);
  }

  function isModifiedClick(event) {
    return event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || (event.button && event.button !== 0);
  }

  VG.prefersReducedMotion = function () {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  };

  VG.dispatch = function (name, detail) {
    document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: detail || {} }));
  };

  /** Runs non-critical work when the main thread is idle (with a timeout so it always runs). */
  VG.idle = function (callback, timeout) {
    if (typeof callback !== 'function') return;
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(
        function () {
          callback();
        },
        { timeout: timeout || 2000 }
      );
    } else {
      window.setTimeout(callback, 200);
    }
  };

  /* Screen reader announcements that must not show a toast (e.g. "Added to your bag"). */
  var announcer = null;
  function getAnnouncer() {
    if (!announcer) {
      announcer = document.createElement('div');
      announcer.className = 'vg-visually-hidden';
      announcer.setAttribute('aria-live', 'polite');
      announcer.setAttribute('aria-atomic', 'true');
      announcer.setAttribute('data-vg-announcer', '');
      document.body.appendChild(announcer);
    }
    return announcer;
  }

  VG.announce = function (message, politeness) {
    if (!message) return;
    var region = getAnnouncer();
    region.setAttribute('aria-live', politeness === 'assertive' ? 'assertive' : 'polite');
    region.textContent = '';
    window.clearTimeout(region._vgTimer);
    region._vgTimer = window.setTimeout(function () {
      region.textContent = message;
    }, 80);
  };

  VG.track = function (name, params) {
    if (typeof window.gtag === 'function') {
      try {
        window.gtag('event', name, params || {});
      } catch (e) {
        /* analytics must never break the page */
      }
    }
  };

  VG.storage = {
    get: function (key, fallback) {
      try {
        var raw = window.localStorage.getItem(key);
        return raw === null ? fallback : JSON.parse(raw);
      } catch (e) {
        return fallback;
      }
    },
    set: function (key, value) {
      try {
        window.localStorage.setItem(key, JSON.stringify(value));
        return true;
      } catch (e) {
        return false;
      }
    },
  };

  var loadedScripts = {};
  VG.loadScript = function (src) {
    if (!src) return Promise.resolve();
    if (loadedScripts[src]) return loadedScripts[src];
    if (document.querySelector('script[src="' + src + '"]')) {
      loadedScripts[src] = Promise.resolve();
      return loadedScripts[src];
    }
    loadedScripts[src] = new Promise(function (resolve, reject) {
      var script = document.createElement('script');
      script.src = src;
      script.defer = true;
      script.onload = function () {
        resolve();
      };
      script.onerror = function () {
        delete loadedScripts[src];
        reject(new Error('Failed to load ' + src));
      };
      document.head.appendChild(script);
    });
    return loadedScripts[src];
  };

  var loadedStyles = {};
  VG.loadStyle = function (href) {
    if (!href) return Promise.resolve();
    if (loadedStyles[href]) return loadedStyles[href];
    var existing = toArray(document.querySelectorAll('link[rel="stylesheet"]')).filter(function (link) {
      return link.getAttribute('href') === href;
    })[0];
    if (existing) {
      loadedStyles[href] = Promise.resolve();
      return loadedStyles[href];
    }
    loadedStyles[href] = new Promise(function (resolve) {
      var link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      link.onload = function () {
        resolve();
      };
      link.onerror = function () {
        resolve();
      };
      document.head.appendChild(link);
    });
    return loadedStyles[href];
  };

  /* Loads Dawn's quick add + product form assets on templates that did not include them. */
  VG.ensureQuickAdd = function () {
    var assets = config.assets || {};
    var jobs = [];
    if (assets.quickAddCss) jobs.push(VG.loadStyle(assets.quickAddCss));
    if (window.customElements) {
      if (!customElements.get('product-form') && assets.productFormJs) jobs.push(VG.loadScript(assets.productFormJs));
      if (!customElements.get('quick-add-modal') && assets.quickAddJs) jobs.push(VG.loadScript(assets.quickAddJs));
    }
    return Promise.all(jobs).catch(function () {
      return null;
    });
  };

  /* ---------- Counts ---------- */
  function setCount(el, count) {
    var display = count > 99 ? '99+' : String(count);
    var format = el.getAttribute('data-vg-count-format');
    el.textContent = format ? format.replace('#', display) : display;
    el.hidden = count <= 0;
  }

  VG.setCartCount = function (count) {
    var value = parseInt(count, 10) || 0;
    toArray(document.querySelectorAll('[data-vg-cart-count]')).forEach(function (el) {
      setCount(el, value);
    });
  };

  var cartCountTimer = null;
  VG.refreshCartCount = function () {
    window.clearTimeout(cartCountTimer);
    cartCountTimer = window.setTimeout(function () {
      fetch(VG.routes.cart + '.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then(function (response) {
          return response.ok ? response.json() : null;
        })
        .then(function (cart) {
          if (cart && typeof cart.item_count === 'number') VG.setCartCount(cart.item_count);
        })
        .catch(function () {
          return null;
        });
    }, 120);
  };

  /* ---------- Toast ---------- */
  function getToastRegion() {
    var region = document.querySelector('[data-vg-toast-region]');
    if (!region) {
      region = document.createElement('div');
      region.className = 'vg-toast-region';
      region.setAttribute('data-vg-toast-region', '');
      region.setAttribute('role', 'status');
      region.setAttribute('aria-live', 'polite');
      region.setAttribute('aria-atomic', 'true');
      document.body.appendChild(region);
    }
    return region;
  }

  /**
   * VG.toast('Saved to your wishlist', { icon: 'heart', duration: 3200, action: { label: 'View', onClick: fn } })
   * Returns { dismiss }.
   */
  VG.toast = function (message, options) {
    var opts = options || {};
    var region = getToastRegion();
    var toast = document.createElement('div');
    toast.className = 'vg-toast';

    var iconName = opts.icon === undefined ? 'check' : opts.icon;
    if (iconName && ICONS[iconName]) {
      var icon = document.createElement('span');
      icon.className = 'vg-toast__icon';
      icon.innerHTML = ICONS[iconName];
      toast.appendChild(icon);
    }

    var text = document.createElement('span');
    text.className = 'vg-toast__message';
    text.textContent = message;
    toast.appendChild(text);

    var timer = null;
    var removed = false;

    function dismiss() {
      if (removed) return;
      removed = true;
      window.clearTimeout(timer);
      toast.classList.remove('is-visible');
      window.setTimeout(function () {
        if (toast.parentNode) toast.parentNode.removeChild(toast);
      }, 350);
    }

    function start() {
      window.clearTimeout(timer);
      timer = window.setTimeout(dismiss, opts.duration || 3200);
    }

    if (opts.action && opts.action.label) {
      var action = document.createElement('button');
      action.type = 'button';
      action.className = 'vg-toast__action';
      action.textContent = opts.action.label;
      action.addEventListener('click', function (event) {
        event.preventDefault();
        dismiss();
        if (typeof opts.action.onClick === 'function') opts.action.onClick(event);
      });
      toast.appendChild(action);
    }

    toast.addEventListener('mouseenter', function () {
      window.clearTimeout(timer);
    });
    toast.addEventListener('mouseleave', start);
    toast.addEventListener('focusin', function () {
      window.clearTimeout(timer);
    });
    toast.addEventListener('focusout', start);

    region.appendChild(toast);
    while (region.children.length > 2) region.removeChild(region.firstElementChild);

    void toast.offsetWidth;
    toast.classList.add('is-visible');
    start();

    return { dismiss: dismiss, element: toast };
  };

  /* ---------- Clipboard ---------- */
  function legacyCopy(text) {
    return new Promise(function (resolve, reject) {
      var area = document.createElement('textarea');
      area.value = text;
      area.setAttribute('readonly', '');
      area.style.position = 'fixed';
      area.style.top = '-1000px';
      area.style.opacity = '0';
      document.body.appendChild(area);
      area.select();
      var ok = false;
      try {
        ok = document.execCommand('copy');
      } catch (e) {
        ok = false;
      }
      document.body.removeChild(area);
      if (ok) resolve();
      else reject(new Error('Copy failed'));
    });
  }

  VG.copy = function (text) {
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(text).catch(function () {
        return legacyCopy(text);
      });
    }
    return legacyCopy(text);
  };

  function onCopyClick(button) {
    var value = button.getAttribute('data-vg-copy');
    if (!value) return;

    VG.copy(value)
      .then(function () {
        var isLink = /^https?:\/\//i.test(value);
        var label = button.querySelector('[data-vg-copy-label]');
        var icon = button.querySelector('.vg-icon--copy');
        var message =
          button.getAttribute('data-vg-copy-message') || (isLink ? t('copiedLink', 'Link copied') : t('copiedCode', 'Code copied') + ': ' + value);

        window.clearTimeout(button._vgCopyTimer);
        if (label && button._vgCopyLabel === undefined) button._vgCopyLabel = label.textContent;
        if (icon && button._vgCopyIcon === undefined) button._vgCopyIcon = icon.innerHTML;

        button.classList.add('is-copied');
        if (label) label.textContent = t('copied', 'Copied');
        if (icon) icon.innerHTML = '<path d="m5 12.5 4.5 4.5L19 7.5"/>';

        VG.toast(message, { icon: 'check', duration: 2400 });

        button._vgCopyTimer = window.setTimeout(function () {
          button.classList.remove('is-copied');
          if (label && button._vgCopyLabel !== undefined) label.textContent = button._vgCopyLabel;
          if (icon && button._vgCopyIcon !== undefined) icon.innerHTML = button._vgCopyIcon;
          button._vgCopyLabel = undefined;
          button._vgCopyIcon = undefined;
        }, 2000);
      })
      .catch(function () {
        VG.toast(t('copyFailed', 'Could not copy. Please copy it manually.'), { icon: 'info', duration: 4000 });
      });
  }

  /* ---------- Wishlist store ---------- */
  var WISHLIST_KEY = 'vg:wishlist';
  var WISHLIST_MAX = 60;
  var wishlistEnabled = config.wishlist !== false;

  function normaliseHandles(list) {
    var seen = {};
    var out = [];
    (Array.isArray(list) ? list : []).forEach(function (item) {
      if (typeof item !== 'string') return;
      var handle = item.trim();
      if (!handle || handle.length > 255 || /[\/?#\s]/.test(handle) || seen[handle]) return;
      seen[handle] = true;
      out.push(handle);
    });
    return out.slice(0, WISHLIST_MAX);
  }

  function syncWishlistUI(list, scope) {
    var handles = list || VG.wishlist.get();
    var root = scope || document;

    toArray(root.querySelectorAll('[data-vg-wishlist-toggle]')).forEach(function (button) {
      var handle = button.getAttribute('data-product-handle');
      if (!handle) return;
      var active = handles.indexOf(handle) > -1;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');

      var label = button.querySelector('[data-vg-wishlist-label]');
      if (label) {
        label.textContent = active
          ? button.getAttribute('data-label-active') || 'Saved to wishlist'
          : button.getAttribute('data-label-inactive') || t('wishlistAdd', 'Add to wishlist');
      } else if (!button.hasAttribute('data-vg-keep-label')) {
        button.setAttribute('aria-label', active ? t('wishlistRemove', 'Remove from wishlist') : t('wishlistAdd', 'Add to wishlist'));
      }
    });

    if (!scope) {
      toArray(document.querySelectorAll('[data-vg-wishlist-count]')).forEach(function (el) {
        setCount(el, handles.length);
      });
    }
  }

  function emitWishlist(list, meta) {
    var info = meta || {};
    syncWishlistUI(list);
    VG.dispatch('vg:wishlist:change', {
      handles: list.slice(),
      count: list.length,
      handle: info.handle || null,
      action: info.action || 'set',
    });
  }

  VG.wishlist = {
    key: WISHLIST_KEY,
    enabled: wishlistEnabled,
    get: function () {
      return normaliseHandles(VG.storage.get(WISHLIST_KEY, []));
    },
    has: function (handle) {
      return this.get().indexOf(handle) > -1;
    },
    count: function () {
      return this.get().length;
    },
    set: function (list, meta) {
      var handles = normaliseHandles(list);
      VG.storage.set(WISHLIST_KEY, handles);
      emitWishlist(handles, meta);
      return handles;
    },
    add: function (handle) {
      if (!handle) return false;
      var list = this.get();
      if (list.indexOf(handle) > -1) return true;
      list.unshift(handle);
      this.set(list, { handle: handle, action: 'add' });
      return true;
    },
    remove: function (handle) {
      var list = this.get();
      var index = list.indexOf(handle);
      if (index === -1) return false;
      list.splice(index, 1);
      this.set(list, { handle: handle, action: 'remove' });
      return true;
    },
    toggle: function (handle) {
      if (this.has(handle)) {
        this.remove(handle);
        return false;
      }
      this.add(handle);
      return true;
    },
    sync: function (scope) {
      syncWishlistUI(null, scope);
    },
    max: WISHLIST_MAX,
    normalise: normaliseHandles,
    /* Full wishlist page (alternate collection template, no admin setup needed). */
    pageUrl: function () {
      return config.wishlistUrl || VG.routes.allProducts + '?view=wishlist';
    },
    /* Absolute link that opens the wishlist page with these handles offered for import. */
    shareUrl: function (handles) {
      var list = normaliseHandles(handles || this.get());
      var url = new URL(this.pageUrl(), window.location.origin);
      if (list.length) url.searchParams.set('items', list.join(','));
      return url.toString().replace(/%2C/gi, ',');
    },
  };

  window.addEventListener('storage', function (event) {
    if (event.key === WISHLIST_KEY) emitWishlist(VG.wishlist.get(), { action: 'sync' });
  });

  function popHeart(button) {
    if (VG.prefersReducedMotion()) return;
    button.classList.remove('is-popping');
    void button.offsetWidth;
    button.classList.add('is-popping');
    window.clearTimeout(button._vgPopTimer);
    button._vgPopTimer = window.setTimeout(function () {
      button.classList.remove('is-popping');
    }, 600);
  }

  function onWishlistToggle(button) {
    var handle = button.getAttribute('data-product-handle');
    if (!handle) return;
    var drawer = closestEl(button, 'vg-wishlist-drawer');
    var inDrawer = !!drawer;
    if (drawer && VG.wishlist.has(handle) && typeof drawer.focusAfterRemove === 'function') {
      drawer.focusAfterRemove(button);
    }
    var added = VG.wishlist.toggle(handle);

    if (added) {
      popHeart(button);
      VG.track('add_to_wishlist', { items: [{ item_id: handle }] });
      VG.toast(t('wishlistAdded', 'Saved to your wishlist'), {
        icon: 'heart',
        action: inDrawer
          ? null
          : {
              label: t('wishlistView', 'View'),
              onClick: function () {
                VG.openWishlist(button);
              },
            },
      });
    } else if (!inDrawer) {
      VG.toast(t('wishlistRemoved', 'Removed from your wishlist'), {
        icon: 'heartOutline',
        action: {
          label: t('wishlistUndo', 'Undo'),
          onClick: function () {
            VG.wishlist.add(handle);
          },
        },
      });
    }
  }

  /* ---------- Card fragments (wishlist, recently viewed) ---------- */
  var cardCache = {};
  var cardQueue = [];
  var cardActive = 0;
  var CARD_CONCURRENCY = 4;

  function pumpCardQueue() {
    while (cardActive < CARD_CONCURRENCY && cardQueue.length) {
      var job = cardQueue.shift();
      cardActive += 1;
      job().then(
        function () {
          cardActive -= 1;
          pumpCardQueue();
        },
        function () {
          cardActive -= 1;
          pumpCardQueue();
        }
      );
    }
  }

  /**
   * Resolves with the inner HTML of .vg-card-fragment for a product handle,
   * or null when the product no longer exists. Rejects on network errors.
   */
  VG.fetchCard = function (handle) {
    if (cardCache[handle]) return cardCache[handle];
    var section = config.cardFragmentSection || 'vg-card-fragment';
    var url = shopRoot + 'products/' + encodeURIComponent(handle) + '?section_id=' + encodeURIComponent(section);

    var promise = new Promise(function (resolve, reject) {
      cardQueue.push(function () {
        return fetch(url, { credentials: 'same-origin' })
          .then(function (response) {
            if (response.status === 404) return null;
            if (!response.ok) throw new Error('Card request failed: ' + response.status);
            return response.text();
          })
          .then(function (text) {
            if (text === null) return resolve(null);
            var doc = new DOMParser().parseFromString(text, 'text/html');
            var fragment = doc.querySelector('.vg-card-fragment');
            if (!fragment || !fragment.children.length) return resolve(null);
            resolve(fragment.innerHTML);
          })
          .catch(function (error) {
            reject(error);
          });
      });
      pumpCardQueue();
    });

    cardCache[handle] = promise;
    promise.catch(function () {
      delete cardCache[handle];
    });
    return promise;
  };

  /**
   * Turns fragment HTML into a DocumentFragment, waiting for the card stylesheets first
   * so cards never flash unstyled. Marks quick add modals so they stack above VG drawers.
   */
  VG.prepareCard = function (html, owner) {
    var template = document.createElement('template');
    template.innerHTML = html;
    var content = template.content;
    var styles = toArray(content.querySelectorAll('link[rel="stylesheet"]'));
    var jobs = styles.map(function (link) {
      var href = link.getAttribute('href');
      link.parentNode.removeChild(link);
      return VG.loadStyle(href);
    });
    if (content.querySelector('quick-add-modal, product-form, modal-opener')) jobs.push(VG.ensureQuickAdd());
    toArray(content.querySelectorAll('quick-add-modal')).forEach(function (modal) {
      modal.classList.add('vg-qa-elevated');
      if (owner) modal.setAttribute('data-vg-owner', owner);
    });
    return Promise.all(jobs).then(function () {
      return content;
    });
  };

  /* ---------- Focus helpers (Dawn's trapFocus when available) ---------- */
  function trap(container, focusEl) {
    if (typeof window.trapFocus === 'function') {
      window.trapFocus(container, focusEl || container);
    } else if (focusEl || container) {
      (focusEl || container).focus();
    }
  }

  function untrap(returnTo) {
    if (typeof window.removeTrapFocus === 'function') {
      window.removeTrapFocus(returnTo || null);
    } else if (returnTo && typeof returnTo.focus === 'function') {
      returnTo.focus();
    }
  }

  function otherOverlayOpen(except) {
    var open = toArray(
      document.querySelectorAll('cart-drawer.active, quick-add-modal[open], modal-dialog[open], details-modal details[open]')
    );
    return open.some(function (el) {
      return el !== except && !(except && except.contains(el));
    });
  }

  /* ---------- Header menu drawer helpers ---------- */
  function closeHeaderDrawer(drawer, event) {
    if (!drawer || typeof drawer.closeMenuDrawer !== 'function') return null;
    var details = drawer.mainDetailsToggle || drawer.querySelector('details');
    if (!details || !details.hasAttribute('open')) return null;
    if (!details.classList.contains('menu-opening')) return null;
    var summary = details.querySelector('summary');
    drawer.closeMenuDrawer(event || new Event('click'), summary);
    return summary;
  }

  function closeOpenHeaderMenus(event) {
    var focusTarget = null;
    toArray(document.querySelectorAll('header-drawer')).forEach(function (drawer) {
      var summary = closeHeaderDrawer(drawer, event);
      if (summary) focusTarget = summary;
    });
    return focusTarget;
  }

  function triggerFor(element) {
    var drawer = closestEl(element, 'header-drawer');
    if (!drawer) return element;
    var summary = drawer.querySelector('summary');
    return summary || element;
  }

  /* ---------- Cart drawer & search (bottom nav, drawer links) ---------- */
  VG.openCart = function (trigger, event) {
    if (config.pageType === 'cart') return false;
    var drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof drawer.open !== 'function') return false;
    var focusTrigger = trigger ? triggerFor(trigger) : null;
    closeOpenHeaderMenus(event);
    VG.closeWishlist(false);
    drawer.open(focusTrigger || undefined);
    return true;
  };

  function isRendered(el) {
    return !!(el && el.getClientRects().length);
  }

  VG.openSearch = function (trigger, event) {
    var modals = toArray(document.querySelectorAll('details-modal.header__search'));
    if (!modals.length) return false;
    var modal =
      modals.filter(function (item) {
        return isRendered(item.querySelector('summary'));
      })[0] || modals[0];
    if (typeof modal.open !== 'function') return false;
    var summary = modal.querySelector('summary');
    if (!summary) return false;

    closeOpenHeaderMenus(event);
    VG.closeWishlist(false);

    var headerSection = document.querySelector('.section-header');
    var sticky = document.querySelector('sticky-header');
    var headerHidden = headerSection && headerSection.classList.contains('shopify-section-header-hidden');
    if (headerHidden && sticky && typeof sticky.reveal === 'function') {
      // reveal() animates `top`, so measuring right after it would still see the hidden header and jump to the top.
      sticky.reveal();
    } else if (!sticky) {
      var rect = modal.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > window.innerHeight) {
        window.scrollTo(0, 0);
      }
    }

    modal.open({ target: summary });
    return true;
  };

  /* ---------- Wishlist drawer ---------- */
  VG.openWishlist = function (trigger) {
    // On the full wishlist page the drawer would only repeat the page: bring the page into view instead.
    var page = document.querySelector('vg-wishlist-page');
    if (page && typeof page.focusPage === 'function') {
      closeOpenHeaderMenus();
      page.focusPage();
      return true;
    }
    var drawer = document.querySelector('vg-wishlist-drawer');
    if (!drawer || typeof drawer.open !== 'function') return false;
    drawer.open(trigger);
    return true;
  };

  VG.closeWishlist = function (returnFocus) {
    var drawer = document.querySelector('vg-wishlist-drawer');
    if (drawer && typeof drawer.close === 'function') drawer.close(returnFocus);
  };

  var wishlistUid = 0;

  class VgWishlistDrawer extends HTMLElement {
    connectedCallback() {
      if (this._vgReady) return;
      this._vgReady = true;

      this.panel = this.querySelector('[data-vg-wishlist-panel]');
      this.grid = this.querySelector('[data-vg-wishlist-grid]');
      this.empty = this.querySelector('[data-vg-wishlist-empty]');
      this.footer = this.querySelector('[data-vg-wishlist-footer]');
      this.body = this.querySelector('[data-vg-wishlist-body]');
      this.skeleton = this.querySelector('template[data-vg-wishlist-skeleton]');
      this.items = {};
      this.dirty = true;

      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('keyup', function (event) {
        if (event.key === 'Escape' || event.code === 'Escape') this.close();
      }.bind(this));

      document.addEventListener(
        'vg:wishlist:change',
        function () {
          if (this.isOpen()) this.render();
          else this.dirty = true;
        }.bind(this)
      );

      document.body.addEventListener(
        'modalClosed',
        function () {
          if (!this.isOpen()) return;
          window.setTimeout(
            function () {
              if (!this.isOpen()) return;
              document.body.classList.add('overflow-hidden');
              trap(this.panel, document.activeElement && this.contains(document.activeElement) ? document.activeElement : this.panel);
            }.bind(this),
            0
          );
        }.bind(this)
      );
    }

    isOpen() {
      return this.classList.contains('is-open');
    }

    setExpanded(expanded) {
      toArray(document.querySelectorAll('[data-vg-wishlist-open][aria-expanded]')).forEach(function (el) {
        el.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      });
    }

    open(trigger) {
      if (this.isOpen()) return;
      this.trigger = trigger ? triggerFor(trigger) : document.activeElement;
      closeOpenHeaderMenus();
      var cart = document.querySelector('cart-drawer.active');
      if (cart && typeof cart.close === 'function') cart.close();

      if (this.dirty || !this.grid.children.length) this.render();
      this.classList.add('is-open');
      document.body.classList.add('overflow-hidden');
      this.setExpanded(true);
      if (this.body) this.body.scrollTop = 0;

      window.setTimeout(
        function () {
          if (this.isOpen()) trap(this.panel, this.panel);
        }.bind(this),
        30
      );
    }

    close(returnFocus) {
      if (!this.isOpen()) return;
      this.classList.remove('is-open');
      if (!otherOverlayOpen(this)) document.body.classList.remove('overflow-hidden');
      this.setExpanded(false);
      untrap(returnFocus === false ? null : this.trigger);
    }

    onClick(event) {
      var target = event.target;
      if (closestEl(target, '[data-vg-wishlist-close]')) {
        event.preventDefault();
        this.close();
        return;
      }

      var remove = closestEl(target, '[data-vg-wishlist-remove]');
      if (remove) {
        event.preventDefault();
        this.focusAfterRemove(remove);
        VG.wishlist.remove(remove.getAttribute('data-vg-wishlist-remove'));
        return;
      }

      var opener = closestEl(target, 'modal-opener[data-modal]');
      if (opener) {
        var modal = document.querySelector(opener.getAttribute('data-modal'));
        if (modal) modal.classList.add('vg-qa-elevated');
      }
    }

    focusAfterRemove(element) {
      var item = closestEl(element, '.vg-wishlist-item');
      if (!item || !this.grid) return;
      var children = toArray(this.grid.children);
      var index = children.indexOf(item);
      var usable = function (el) {
        return el !== item && !el.classList.contains('is-removing');
      };
      var sibling = children.slice(index + 1).filter(usable)[0] || children.slice(0, index).filter(usable).pop() || null;
      var nextFocus = sibling ? sibling.querySelector('[data-vg-wishlist-remove]') : null;

      window.setTimeout(
        function () {
          if (!this.isOpen()) return;
          if (nextFocus && document.contains(nextFocus)) {
            nextFocus.focus();
          } else {
            var cta = this.empty && !this.empty.hidden ? this.empty.querySelector('a, button') : null;
            (cta || this.panel).focus();
          }
        }.bind(this),
        280
      );
    }

    render() {
      this.dirty = false;
      var handles = VG.wishlist.get();
      var hasItems = handles.length > 0;

      this.empty.hidden = hasItems;
      this.grid.hidden = !hasItems;
      if (this.footer) this.footer.hidden = !hasItems;

      Object.keys(this.items).forEach(
        function (handle) {
          if (handles.indexOf(handle) === -1) this.removeItem(handle);
        }.bind(this)
      );

      handles.forEach(
        function (handle) {
          if (!this.items[handle]) {
            var item = this.createPlaceholder(handle);
            this.items[handle] = item;
            this.grid.appendChild(item);
            this.load(handle, item);
          }
        }.bind(this)
      );

      var current = toArray(this.grid.children)
        .filter(function (el) {
          return !el.classList.contains('is-removing');
        })
        .map(function (el) {
          return el.getAttribute('data-handle');
        });
      if (current.join('|') !== handles.join('|')) {
        handles.forEach(
          function (handle) {
            this.grid.appendChild(this.items[handle]);
          }.bind(this)
        );
      }
    }

    createPlaceholder(handle) {
      var item;
      if (this.skeleton && this.skeleton.content.firstElementChild) {
        item = this.skeleton.content.firstElementChild.cloneNode(true);
      } else {
        item = document.createElement('li');
        item.className = 'vg-wishlist-item vg-wishlist-item--loading';
      }
      item.setAttribute('data-handle', handle);
      return item;
    }

    removeButton(handle, title) {
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'vg-wishlist-item__remove';
      button.setAttribute('data-vg-wishlist-remove', handle);
      button.setAttribute('aria-label', title ? 'Remove ' + title + ' from wishlist' : t('wishlistRemove', 'Remove from wishlist'));
      button.innerHTML = ICONS.close;
      var text = document.createElement('span');
      text.textContent = t('wishlistRemoveText', 'Remove');
      button.appendChild(text);
      return button;
    }

    load(handle, item) {
      var drawer = this;
      VG.fetchCard(handle)
        .then(function (html) {
          if (drawer.items[handle] !== item) return null;
          if (html === null) {
            VG.wishlist.remove(handle);
            return null;
          }
          return VG.prepareCard(html, 'wishlist').then(function (content) {
            if (drawer.items[handle] !== item) return;
            wishlistUid += 1;
            var wrapper = document.createElement('div');
            wrapper.className = 'shopify-section vg-wishlist-item__card';
            wrapper.id = 'shopify-section-vg-wishlist-' + wishlistUid;
            wrapper.appendChild(content);

            var heading = wrapper.querySelector('.card__heading');
            var title = heading ? heading.textContent.replace(/\s+/g, ' ').trim() : '';

            item.className = 'vg-wishlist-item';
            item.removeAttribute('aria-hidden');
            item.innerHTML = '';
            item.appendChild(wrapper);
            item.appendChild(drawer.removeButton(handle, title));
            syncWishlistUI(null, item);
          });
        })
        .catch(function () {
          if (drawer.items[handle] !== item) return;
          item.className = 'vg-wishlist-item vg-wishlist-item--fallback';
          item.removeAttribute('aria-hidden');
          item.innerHTML = '';
          var link = document.createElement('a');
          link.className = 'vg-link';
          link.href = shopRoot + 'products/' + encodeURIComponent(handle);
          link.textContent = 'View product';
          item.appendChild(link);
          item.appendChild(drawer.removeButton(handle, ''));
        });
    }

    removeItem(handle) {
      var item = this.items[handle];
      delete this.items[handle];
      if (!item) return;

      var cleanup = function () {
        if (item.parentNode) item.parentNode.removeChild(item);
        pruneOwnedModals();
      };

      if (!this.isOpen() || VG.prefersReducedMotion()) {
        cleanup();
        return;
      }
      item.classList.add('is-removing');
      window.setTimeout(cleanup, 260);
    }
  }

  /* Quick add modals move themselves to <body>; drop the ones whose wishlist card is gone. */
  function pruneOwnedModals() {
    var drawer = document.querySelector('vg-wishlist-drawer');
    var openers = drawer ? toArray(drawer.querySelectorAll('modal-opener[data-modal]')) : [];
    var wanted = openers.map(function (opener) {
      return opener.getAttribute('data-modal');
    });
    toArray(document.querySelectorAll('quick-add-modal[data-vg-owner="wishlist"]')).forEach(function (modal) {
      if (modal.hasAttribute('open')) return;
      if (wanted.indexOf('#' + modal.id) === -1) modal.parentNode.removeChild(modal);
    });
  }

  /* ---------- <vg-slider> ---------- */
  class VgSlider extends HTMLElement {
    constructor() {
      super();
      this.update = this.update.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onClick = this.onClick.bind(this);
      this.frame = null;
    }

    connectedCallback() {
      this.track = this.own('[data-vg-slider-track]')[0];
      if (!this.track) return;
      this.prevButtons = this.own('[data-vg-slider-prev]');
      this.nextButtons = this.own('[data-vg-slider-next]');

      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.track.addEventListener('keydown', this.onKeydown);
      this.addEventListener('click', this.onClick);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.onScroll);
        this.resizeObserver.observe(this.track);
      } else {
        window.addEventListener('resize', this.onScroll);
      }
      this.mutationObserver = new MutationObserver(this.onScroll);
      this.mutationObserver.observe(this.track, { childList: true });

      this.update();
    }

    disconnectedCallback() {
      if (!this.track) return;
      this.track.removeEventListener('scroll', this.onScroll);
      this.track.removeEventListener('keydown', this.onKeydown);
      this.removeEventListener('click', this.onClick);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      else window.removeEventListener('resize', this.onScroll);
      if (this.mutationObserver) this.mutationObserver.disconnect();
      if (this.frame) window.cancelAnimationFrame(this.frame);
    }

    own(selector) {
      var host = this;
      return toArray(this.querySelectorAll(selector)).filter(function (el) {
        return el.closest('vg-slider') === host;
      });
    }

    get slides() {
      return toArray(this.track.children).filter(function (el) {
        return el.getClientRects().length > 0;
      });
    }

    isRTL() {
      return window.getComputedStyle(this.track).direction === 'rtl';
    }

    gap() {
      var style = window.getComputedStyle(this.track);
      return parseFloat(style.columnGap) || parseFloat(style.gap) || 0;
    }

    step() {
      var first = this.slides[0];
      return first ? first.getBoundingClientRect().width + this.gap() : this.track.clientWidth;
    }

    maxScroll() {
      return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    }

    position() {
      return Math.abs(this.track.scrollLeft);
    }

    behavior() {
      return VG.prefersReducedMotion() ? 'auto' : 'smooth';
    }

    go(direction) {
      var sign = this.isRTL() ? -1 : 1;
      this.track.scrollBy({ left: direction * this.step() * sign, behavior: this.behavior() });
    }

    next() {
      this.go(1);
    }

    prev() {
      this.go(-1);
    }

    scrollToSlide(slide) {
      if (!slide || slide.parentElement !== this.track) return;
      var trackRect = this.track.getBoundingClientRect();
      var slideRect = slide.getBoundingClientRect();
      var delta = this.isRTL() ? slideRect.right - trackRect.right : slideRect.left - trackRect.left;
      this.track.scrollBy({ left: delta, behavior: this.behavior() });
    }

    onClick(event) {
      var button = closestEl(event.target, '[data-vg-slider-prev], [data-vg-slider-next]');
      if (!button || button.closest('vg-slider') !== this) return;
      event.preventDefault();
      if (button.hasAttribute('data-vg-slider-next')) this.next();
      else this.prev();
    }

    onKeydown(event) {
      if (event.defaultPrevented || event.target !== this.track) return;
      if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
      event.preventDefault();
      var forward = event.key === 'ArrowRight';
      if (this.isRTL()) forward = !forward;
      this.go(forward ? 1 : -1);
    }

    onScroll() {
      if (this.frame) return;
      this.frame = window.requestAnimationFrame(
        function () {
          this.frame = null;
          this.update();
        }.bind(this)
      );
    }

    setDisabled(buttons, disabled, fallbackFocus) {
      buttons.forEach(function (button) {
        if (disabled && document.activeElement === button && fallbackFocus) fallbackFocus.focus();
        button.disabled = disabled;
        button.classList.toggle('is-disabled', disabled);
      });
    }

    update() {
      if (!this.track || !this.isConnected) return;
      var max = this.maxScroll();
      var pos = this.position();
      var isStatic = max <= 2;
      var atStart = pos <= 2;
      var atEnd = pos >= max - 2;

      this.classList.toggle('is-static', isStatic);
      this.classList.toggle('is-start', atStart);
      this.classList.toggle('is-end', atEnd);

      this.setDisabled(this.prevButtons, isStatic || atStart, this.nextButtons[0]);
      this.setDisabled(this.nextButtons, isStatic || atEnd, this.prevButtons[0]);

      var size = this.track.scrollWidth ? this.track.clientWidth / this.track.scrollWidth : 1;
      this.style.setProperty('--vg-progress', max ? (pos / max).toFixed(4) : '0');
      this.style.setProperty('--vg-progress-size', Math.min(1, size).toFixed(4));

      if (!isStatic && !this.track.hasAttribute('tabindex') && !this.track.querySelector('a[href], button, input, select, textarea, [tabindex]')) {
        this.track.setAttribute('tabindex', '0');
        if (!this.track.hasAttribute('aria-label') && !this.track.hasAttribute('aria-labelledby')) {
          this.track.setAttribute('aria-label', this.getAttribute('aria-label') || 'Scrollable list');
        }
      }

      if (this._vgState !== [isStatic, atStart, atEnd].join()) {
        this._vgState = [isStatic, atStart, atEnd].join();
        this.dispatchEvent(new CustomEvent('vg:slider:change', { bubbles: true, detail: { atStart: atStart, atEnd: atEnd, isStatic: isStatic } }));
      }
    }
  }

  if (!customElements.get('vg-slider')) customElements.define('vg-slider', VgSlider);
  if (!customElements.get('vg-wishlist-drawer')) customElements.define('vg-wishlist-drawer', VgWishlistDrawer);

  /* ==========================================================================
     Pause decorative looping animations while off screen (marquee, hotspot halos, hero zoom)
     so they never compete with scrolling. Re-scans after section re-renders / theme editor loads.
     ========================================================================== */
  var PAUSE_SELECTOR = '.vg-marquee, .vg-stl, .vg-hero, [data-vg-pause-offscreen]';
  var pauseObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              entry.target.classList.toggle('vg-offscreen', !entry.isIntersecting);
            });
          },
          { rootMargin: '120px 0px' }
        )
      : null;

  function observePausables(root) {
    if (!pauseObserver) return;
    var scope = root && root.querySelectorAll ? root : document;
    toArray(scope.querySelectorAll(PAUSE_SELECTOR)).forEach(function (el) {
      if (el.__vgPauseObserved) return;
      el.__vgPauseObserved = true;
      pauseObserver.observe(el);
    });
  }

  observePausables(document);
  document.addEventListener('shopify:section:load', function (event) {
    observePausables(event.target);
  });

  /* ==========================================================================
     Image skeleton + fade (contract 3)
     Any wrapper with .vg-img shows a champagne shimmer (vg-base.css) until an <img>
     inside it has loaded; then it gets .is-loaded and lazy images fade in.
     Nothing is hidden before this script runs: complete images are marked first,
     then html.js-vg-img switches the CSS on.
     ========================================================================== */
  var IMG_WRAP = '.vg-img';

  function markImageLoaded(img) {
    var wrap = closestEl(img, IMG_WRAP);
    if (wrap && !wrap.classList.contains('is-loaded')) wrap.classList.add('is-loaded');
  }

  function onImageEvent(event) {
    var target = event.target;
    if (target && target.tagName === 'IMG') markImageLoaded(target);
  }

  // Capture phase: load/error do not bubble.
  document.addEventListener('load', onImageEvent, true);
  document.addEventListener('error', onImageEvent, true);

  function collect(root, selector) {
    var scope = root || document;
    var list = scope.querySelectorAll ? toArray(scope.querySelectorAll(selector)) : [];
    if (scope.matches && scope.matches(selector)) list.unshift(scope);
    return list;
  }

  VG.images = {
    scan: function (root) {
      collect(root, IMG_WRAP + ':not(.is-loaded)').forEach(function (wrap) {
        var images = wrap.getElementsByTagName('img');
        if (!images.length) {
          wrap.classList.add('is-loaded');
          return;
        }
        for (var i = 0; i < images.length; i += 1) {
          if (images[i].complete) {
            wrap.classList.add('is-loaded');
            return;
          }
        }
      });
    },
  };

  VG.images.scan(document);
  docEl.classList.add('js-vg-img');

  /* ==========================================================================
     Reveal on scroll (contract 2)
     [data-vg-reveal] (+ optional style="--vg-stagger: N") fades and rises in once.
     Elements already on screen when this runs are shown as they are (no flash),
     then html.js-vg-reveal hides the rest until they scroll into view.
     Off with Dawn's "Reveal sections on scroll" setting or prefers-reduced-motion.
     ========================================================================== */
  var REVEAL_SELECTOR = '[data-vg-reveal]';
  var revealEnabled = config.reveal !== false && 'IntersectionObserver' in window && !VG.prefersReducedMotion();
  var revealObserver = null;

  function onRevealEnd(event) {
    if (event.target !== this) return;
    this.classList.remove('is-revealing');
    this.removeEventListener('animationend', onRevealEnd);
  }

  /* animate: run the fade + rise once (.is-revealing is dropped afterwards so a later display change
     never replays it and the element's own transforms/transitions are untouched). */
  function revealElement(el, animate) {
    if (revealObserver) revealObserver.unobserve(el);
    if (el.classList.contains('is-revealed')) return;
    if (animate === true) {
      el.addEventListener('animationend', onRevealEnd);
      el.classList.add('is-revealing');
      window.setTimeout(function () {
        el.classList.remove('is-revealing');
      }, 2600);
    }
    el.classList.add('is-revealed');
  }

  function revealAnimated(el) {
    revealElement(el, true);
  }

  function onRevealIntersect(entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      // Cards inside a horizontal slider reveal together, so swiping never meets blank slides.
      var track = closestEl(entry.target, '[data-vg-slider-track]');
      if (track) toArray(track.querySelectorAll(REVEAL_SELECTOR)).forEach(revealAnimated);
      else revealAnimated(entry.target);
    });
  }

  VG.reveal = {
    enabled: revealEnabled,
    observe: function (root) {
      var items = collect(root, REVEAL_SELECTOR).filter(function (el) {
        return !el.classList.contains('is-revealed');
      });
      if (!items.length) return;
      if (!revealEnabled) {
        items.forEach(revealElement);
        return;
      }
      if (!revealObserver) {
        revealObserver = new IntersectionObserver(onRevealIntersect, { rootMargin: '0px 0px -6% 0px', threshold: 0.01 });
      }
      items.forEach(function (el) {
        revealObserver.observe(el);
      });
    },
    revealAll: function (root) {
      collect(root, REVEAL_SELECTOR).forEach(revealElement);
    },
  };

  function initReveal() {
    var items = toArray(document.querySelectorAll(REVEAL_SELECTOR));
    if (!revealEnabled) {
      items.forEach(revealElement);
      return;
    }
    var viewportHeight = window.innerHeight || docEl.clientHeight;
    items.forEach(function (el) {
      var rect = el.getBoundingClientRect();
      if ((rect.width || rect.height) && rect.top < viewportHeight) el.classList.add('is-revealed');
    });
    docEl.classList.add('js-vg-reveal');
    VG.reveal.observe(document);
  }

  /* ==========================================================================
     Cross-document view transitions (contract 4)
     @view-transition is declared in layout/theme.liquid (setting vg_enable_view_transitions).
     Click on a product card: its [data-vg-vt-source] image gets the shared name and the
     intent is stored in sessionStorage; the product page's inline head script adds
     html.vg-vt-morph so its [data-vg-vt-target] photo carries the same name (vg-base.css).
     Names are set on one element at a time and cleared after the transition.
     ========================================================================== */
  var VT_NAME = 'vg-product-media';
  var VT_KEY = 'vg:vt';
  var vtEnabled = config.viewTransitions === true && !designMode && 'onpagereveal' in window;
  var vtNamed = [];
  var vtPending = null;

  function vtSet(el, value) {
    el.style.setProperty('view-transition-name', value);
    vtNamed.push(el);
  }

  function vtClear() {
    vtNamed.forEach(function (el) {
      el.style.removeProperty('view-transition-name');
    });
    vtNamed = [];
    vtPending = null;
  }

  function vtRead() {
    try {
      return JSON.parse(window.sessionStorage.getItem(VT_KEY) || 'null');
    } catch (e) {
      return null;
    }
  }

  function stripHash(href) {
    return String(href || '').split('#')[0];
  }

  /* The card image belonging to a clicked link: the nearest ancestor holding exactly one source. */
  function findVtSource(link) {
    var own = closestEl(link, '[data-vg-vt-source]');
    if (own) return own;
    var node = link.parentElement;
    for (var depth = 0; node && node !== document.body && depth < 10; depth += 1) {
      var sources = node.querySelectorAll('[data-vg-vt-source]');
      if (sources.length === 1) return sources[0];
      if (sources.length > 1) return null;
      node = node.parentElement;
    }
    return null;
  }

  function onVtClick(event) {
    if (event.defaultPrevented || isModifiedClick(event)) return;
    var link = closestEl(event.target, 'a[href]');
    if (!link || (link.target && link.target !== '_self') || link.hasAttribute('download')) return;
    var url;
    try {
      url = new URL(link.href, window.location.href);
    } catch (e) {
      return;
    }
    if (url.origin !== window.location.origin || url.pathname.indexOf('/products/') === -1) return;
    if (url.pathname === window.location.pathname) return;

    vtClear();
    var source = findVtSource(link);
    if (!source) return;
    var rect = source.getBoundingClientRect();
    if (!rect.width || !rect.height || rect.bottom < 0 || rect.top > (window.innerHeight || docEl.clientHeight)) return;

    // Names must be unique: on a product page the page's own photo steps aside for the clicked card.
    toArray(document.querySelectorAll('[data-vg-vt-target]')).forEach(function (el) {
      vtSet(el, 'none');
    });
    vtSet(source, VT_NAME);
    vtPending = { path: url.pathname };
    try {
      window.sessionStorage.setItem(VT_KEY, JSON.stringify({ path: url.pathname, from: window.location.href, ts: Date.now() }));
    } catch (e) {
      /* private mode: the photo simply cross-fades */
    }
  }

  if (vtEnabled) {
    document.addEventListener('click', onVtClick);

    window.addEventListener('pageswap', function (event) {
      if (!event.viewTransition) {
        vtClear();
        return;
      }
      var activation = event.activation;
      var dest = null;
      try {
        dest = activation && activation.entry && activation.entry.url ? new URL(activation.entry.url) : null;
      } catch (e) {
        dest = null;
      }
      // Clicked a card but went somewhere else: no photo morph.
      if (vtPending && dest && dest.pathname !== vtPending.path) vtClear();

      // Product page reached from a card: morph back only when returning to that page.
      if (docEl.classList.contains('vg-vt-morph')) {
        var stored = vtRead();
        var goingBack =
          activation &&
          activation.navigationType === 'traverse' &&
          stored &&
          stored.from &&
          dest &&
          stripHash(dest.href) === stripHash(stored.from);
        if (!goingBack) docEl.classList.remove('vg-vt-morph');
      }
    });

    // Restored from the back/forward cache: drop the names once the (reverse) morph is done.
    window.addEventListener('pagereveal', function (event) {
      if (event.viewTransition && event.viewTransition.finished) {
        event.viewTransition.finished.then(vtClear, vtClear);
      } else {
        vtClear();
      }
    });
  }

  /* ==========================================================================
     Shared button states (contract 6)
     VG.buttonState(button, 'loading' | 'added' | 'idle', { label: 'Added', duration: 1600 })
     .is-loading shows a spinner; .is-added shows a check + label, then returns to idle.
     ========================================================================== */
  function ensureAddedLabel(button, label) {
    var added = button.querySelector(':scope > .vg-btn-added');
    if (!added) {
      added = document.createElement('span');
      added.className = 'vg-btn-added';
      added.setAttribute('aria-hidden', 'true');
      added.innerHTML = ICONS.check + '<span class="vg-btn-added__label"></span>';
      button.appendChild(added);
    }
    added.querySelector('.vg-btn-added__label').textContent = label;
  }

  VG.buttonState = function (button, state, options) {
    if (!button || !button.classList) return;
    var opts = options || {};
    window.clearTimeout(button._vgStateTimer);
    button.classList.remove('is-loading', 'is-added');
    button.removeAttribute('aria-busy');

    if (state === 'loading') {
      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');
    } else if (state === 'added') {
      ensureAddedLabel(button, opts.label || 'Added');
      // Reflow so the check animates even when "added" follows "loading" in the same frame.
      void button.offsetWidth;
      button.classList.add('is-added');
      if (opts.announce) VG.announce(opts.announce);
      button._vgStateTimer = window.setTimeout(function () {
        button.classList.remove('is-added');
        if (typeof opts.onDone === 'function') opts.onDone();
      }, opts.duration || 1600);
    }
  };

  /* ==========================================================================
     Back to top: gold ring after two screens of scrolling
     ========================================================================== */
  function initBackToTop() {
    var button = document.querySelector('[data-vg-back-to-top]');
    if (!button || button._vgReady) return;
    button._vgReady = true;

    var ring = button.querySelector('[data-vg-btt-progress]');
    var ringLength = ring ? parseFloat(ring.getAttribute('data-length')) || 0 : 0;
    var viewportHeight = window.innerHeight;
    var docHeight = docEl.scrollHeight;
    var visible = false;
    var frame = null;

    function measure() {
      viewportHeight = window.innerHeight;
      docHeight = docEl.scrollHeight;
    }

    function update() {
      frame = null;
      var y = window.scrollY || window.pageYOffset || 0;
      var show = y > viewportHeight * 2;
      if (show !== visible) {
        visible = show;
        button.classList.toggle('is-visible', show);
      }
      if (ring && ringLength && visible) {
        var progress = Math.min(1, Math.max(0, y / Math.max(1, docHeight - viewportHeight)));
        ring.style.strokeDashoffset = (ringLength * (1 - progress)).toFixed(2);
      }
    }

    function schedule() {
      if (!frame) frame = window.requestAnimationFrame(update);
    }

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener(
      'resize',
      function () {
        measure();
        schedule();
      },
      { passive: true }
    );
    if ('ResizeObserver' in window) {
      new ResizeObserver(function () {
        measure();
        schedule();
      }).observe(document.body);
    }

    button.addEventListener('click', function (event) {
      window.scrollTo({ top: 0, behavior: VG.prefersReducedMotion() ? 'auto' : 'smooth' });
      // Keyboard users continue from the top of the page content.
      if (event.detail === 0) {
        var main = document.getElementById('MainContent');
        if (main) main.focus({ preventScroll: true });
      }
      VG.track('back_to_top', { page_type: config.pageType || '' });
    });

    update();
  }

  /* Theme editor: bring the selected block's slide into view. */
  document.addEventListener('shopify:block:select', function (event) {
    var slider = closestEl(event.target, 'vg-slider');
    if (!slider || !slider.track) return;
    var slide = event.target;
    while (slide && slide.parentElement !== slider.track) slide = slide.parentElement;
    if (slide) slider.scrollToSlide(slide);
  });

  /* On the cart page the header bag icon stays a plain link (like VG.openCart): the page already shows the bag.
     (Edits on the page now re-render the hidden drawer too, see cart.js.) Capture runs before Dawn's handler. */
  function isCartPageBagIcon(event) {
    return config.pageType === 'cart' && event.target && typeof event.target.closest === 'function' && event.target.closest('#cart-icon-bubble');
  }
  document.addEventListener(
    'click',
    function (event) {
      if (isCartPageBagIcon(event) && !isModifiedClick(event)) event.stopPropagation();
    },
    true
  );
  document.addEventListener(
    'keydown',
    function (event) {
      if (isCartPageBagIcon(event) && event.code && event.code.toUpperCase() === 'SPACE') event.stopPropagation();
    },
    true
  );

  /* ---------- Global click delegation (survives Dawn section re-renders) ---------- */
  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    var drawerClose = target.closest('[data-vg-drawer-close]');
    if (drawerClose && !event.defaultPrevented) {
      closeHeaderDrawer(drawerClose.closest('header-drawer'), event);
    }

    if (wishlistEnabled) {
      var toggle = target.closest('[data-vg-wishlist-toggle]');
      if (toggle) {
        event.preventDefault();
        onWishlistToggle(toggle);
        return;
      }

      var wishlistOpener = target.closest('[data-vg-wishlist-open]');
      if (wishlistOpener) {
        event.preventDefault();
        VG.openWishlist(wishlistOpener);
        return;
      }
    }

    var copyButton = target.closest('[data-vg-copy]');
    if (copyButton) {
      event.preventDefault();
      onCopyClick(copyButton);
      return;
    }

    var cartLink = target.closest('[data-vg-open-cart]');
    if (cartLink) {
      if (!isModifiedClick(event) && VG.openCart(cartLink, event)) event.preventDefault();
      return;
    }

    var searchLink = target.closest('[data-vg-search-open]');
    if (searchLink) {
      if (!isModifiedClick(event) && VG.openSearch(searchLink, event)) event.preventDefault();
      return;
    }

    if (target.closest('[data-vg-wa-float], [data-vg-whatsapp-link]')) {
      VG.track('whatsapp_click', { page_type: config.pageType || '' });
    }
  });

  /* ---------- Cart count via Dawn pub/sub ---------- */
  if (typeof window.subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    window.subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      var data = event && event.cartData;
      if (data && typeof data.item_count === 'number') VG.setCartCount(data.item_count);
      else VG.refreshCartCount();
      if (event && event.source === 'product-form') VG.closeWishlist(false);
    });
  }

  /* ---------- Keep state in sync with dynamically rendered markup ---------- */
  var syncFrame = null;
  var WATCH_SELECTOR = '[data-vg-wishlist-toggle], [data-vg-wishlist-count]';

  function scheduleSync() {
    if (syncFrame) return;
    syncFrame = window.requestAnimationFrame(function () {
      syncFrame = null;
      syncWishlistUI();
    });
  }

  function hasMatch(node, selector) {
    return (node.matches && node.matches(selector)) || (node.querySelector && node.querySelector(selector));
  }

  /* One observer for markup rendered later (Dawn section rendering, AJAX cards, load more, theme editor):
     wishlist hearts/counts, reveal targets and image skeletons. */
  if ('MutationObserver' in window) {
    new MutationObserver(function (mutations) {
      var needsSync = false;
      var roots = [];
      for (var i = 0; i < mutations.length; i += 1) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j += 1) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (wishlistEnabled && !needsSync && hasMatch(node, WATCH_SELECTOR)) needsSync = true;
          if (hasMatch(node, REVEAL_SELECTOR + ', ' + IMG_WRAP)) roots.push(node);
        }
      }
      if (needsSync) scheduleSync();
      roots.forEach(function (root) {
        if (!root.isConnected) return;
        VG.images.scan(root);
        if (docEl.classList.contains('js-vg-reveal') || !revealEnabled) VG.reveal.observe(root);
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  /* Theme editor: re-rendered sections, and show everything in the section being edited. */
  document.addEventListener('shopify:section:load', function (event) {
    VG.images.scan(event.target);
    VG.reveal.observe(event.target);
    if (wishlistEnabled) syncWishlistUI();
    initBackToTop();
  });

  document.addEventListener('shopify:section:select', function (event) {
    VG.reveal.revealAll(event.target);
  });

  document.addEventListener('shopify:block:select', function (event) {
    VG.reveal.revealAll(closestEl(event.target, '.shopify-section') || event.target);
  });

  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;
    if (wishlistEnabled) syncWishlistUI();
    VG.refreshCartCount();
  });

  /* ---------- WhatsApp float: one gentle tooltip peek per session ---------- */
  function initWhatsAppPeek() {
    var fab = document.querySelector('[data-vg-wa-float]');
    if (!fab || (window.Shopify && window.Shopify.designMode)) return;
    var seen = false;
    try {
      seen = window.sessionStorage.getItem('vg:wa-peek') === '1';
    } catch (e) {
      seen = true;
    }
    if (seen) return;
    window.setTimeout(function () {
      if (document.body.classList.contains('overflow-hidden')) return;
      fab.classList.add('is-peek');
      try {
        window.sessionStorage.setItem('vg:wa-peek', '1');
      } catch (e) {
        /* ignore */
      }
      window.setTimeout(function () {
        fab.classList.remove('is-peek');
      }, 4000);
    }, 8000);
  }

  /* ---------- Init ---------- */
  function unmarkCartPageBagIcon() {
    // Dawn's cart-drawer.js (a later deferred script) marks the bag icon as a dialog button; on the cart page it is a link.
    var bagIcon = document.getElementById('cart-icon-bubble');
    if (!bagIcon) return;
    bagIcon.removeAttribute('role');
    bagIcon.removeAttribute('aria-haspopup');
  }
  if (config.pageType === 'cart') {
    if (document.readyState === 'complete') unmarkCartPageBagIcon();
    else document.addEventListener('DOMContentLoaded', unmarkCartPageBagIcon);
  }

  function init() {
    if (wishlistEnabled) syncWishlistUI();
    initReveal();
    VG.idle(initBackToTop, 1500);
    VG.idle(initWhatsAppPeek);
    VG.idle(getAnnouncer);
    VG.dispatch('vg:ready', { version: VG.version });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
